import React from 'react';

import { Loader } from 'semantic-ui-react';
import { WeezHeader, Button, EmptyState, ContentCard, Icon, Card, SelectButton, ButtonDropdown, DropdownItem, Modal, ToastContext, Message } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DiscountTable from './discountTable';
import { handleSearchQuery, useFilters, usePageSize } from '../../manager/components/context';
import { useNotifications, triggerToast } from '../../../components/notifications/notificationsProvider';
import DropdownPageSize from '../../manager/components/dropdownPageSize';
import Style from './discountsList.module.css';
import DiscountCodeModel from '../../../models/discountCodeModel';
import ExportButton from '../../../components/export/exportButton';
import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';
import FilterComponent from './component/filterComponent';

const spec = {
    multiple: ['status__in'],
    fields: ['discount_type', 'usage', 'search'],
    aliases: {},
    date: []
};

/**
 * COmponent to display the discount dashboard
 * @param {object} organization: organisation object to have id orga
 * @param {event} event: event object to have id event
 */

export default function DiscountsList({ organization, event }) {
    const { t } = useTranslation();
    const initialFilters = {
        status__in: new Set(['active', 'inactive', 'unavailable', 'scheduled', 'expired'])
    };

    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [filters, setFilters] = useFilters(spec, initialFilters);
    const [noDiscountCode, setNoDiscountCode] = React.useState(false);
    const [pageSize, setPageSize] = usePageSize();
    const { addNotification } = useNotifications();

    const [currentPage, setCurrentPage] = React.useState(0);
    const [selectedDiscounts, setSelectedDiscounts] = React.useState({});
    const [discountsToDelete, setDiscountsToDelete] = React.useState([]);
    const [ordering, setOrdering] = React.useState('-created');
    const [discounts, setDiscounts] = React.useState([]);

    const [, loading, sync] = DiscountCodeModel.useApiModel(
        {
            eventId: event.pk,
            organizationId: organization.pk,
            query: {
                include_deleted: true,
                offset: currentPage * pageSize,
                expand: 'discount,discount.discount_rules',
                ordering: ordering,
                limit: pageSize,
                sync: true,
                should_not_count: true,
                ...handleSearchQuery(spec, filters)
            },
            allow_cache: false,
            cache: false,
            onSuccess: discounts => {
                if (Object.entries(filters).length === 0 && discounts.length === 0) {
                    setNoDiscountCode(true);
                }
                setDiscounts(discounts.map(discount => ({ ...discount.object, disabled: discount.object.status === 'removed' ? true : false })));
            }
        },
        [pageSize, currentPage, ordering, filters, spec, organization, event]
    );

    const [countDiscounts, loadingCountDiscounts] = DiscountCodeModel.useApiModel(
        {
            eventId: event.pk,
            organizationId: organization.pk,
            query: {
                include_deleted: true,
                limit: 0,
                sync: false,
                ...handleSearchQuery(spec, filters)
            },
            allow_cache: false,
            cache: false
        },
        [filters, spec, organization, event]
    );

    const totalDiscounts = React.useMemo(() => {
        if (!countDiscounts) {
            return 0;
        }
        return Number.parseInt(countDiscounts?.__meta?.headers.get('total-count')) || 0;
    }, [countDiscounts]);

    const { track } = useSegment();

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
            setCurrentPage(0);
        },
        [filters]
    );

    const onSelected = React.useCallback(
        (event, discount) => {
            event.stopPropagation();
            setSelectedDiscounts(currentState => {
                const newSelected = { ...currentState };
                const isSelected = discount.id in currentState;
                if (!event.target?.checked && isSelected) {
                    delete newSelected[discount.id];
                }

                if (event.target?.checked && !isSelected) {
                    newSelected[discount.id] = discount;
                }

                return newSelected;
            });
        },
        [discounts]
    );
    const onSelectAll = React.useCallback(
        event => {
            if (event.target?.checked) {
                let discountsTmp = {};
                discounts.forEach(discount => {
                    discountsTmp[discount.id] = discount;
                });
                setSelectedDiscounts(discountsTmp);
            } else {
                setSelectedDiscounts({});
            }
        },
        [discounts]
    );

    const onPageChange = numberPage => {
        setCurrentPage(numberPage);
    };

    const changeOrder = order => {
        setOrdering(order);
    };

    const onDelete = React.useCallback(() => {
        DiscountCodeModel.performDelete({ discountCodes: discountsToDelete, orgaId: organization.id, eventId: event.pk })
            .then(response => {
                if (discountsToDelete.length === 1) {
                    toast.success(t(`ticket.discountList.delete.success`, { count: 1 }));
                } else {
                    triggerToast('attendees', { async: true }, discountsToDelete.length, response, t, toast, addNotification, `ticket.discountList.delete.`);
                }
            })
            .catch(e => {
                console.error(e);
                toast.error(t(`ticket.discountList.delete.error`, { count: discountsToDelete.length }));
            })
            .finally(() => {
                setSelectedDiscounts({});
                setDiscountsToDelete([]);
                sync();
            });
    }, [sync, toast, addNotification, triggerToast, organization, event, discountsToDelete]);

    const onToggle = React.useCallback(
        (discountCodes, enabled) => {
            track(`ACTION > ${enabled ? 'ACTIVATE' : 'DEACTIVATE'} DISCOUNT`, { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });
            DiscountCodeModel.performToggleActivation(organization.id, event.id, discountCodes, enabled)
                .then(response => {
                    if (discountCodes.length === 1) {
                        toast.success(t(`ticket.discountList.${enabled ? 'enable' : 'disabled'}.success`, { count: 1 }));
                    } else {
                        triggerToast(
                            'attendees',
                            { async: true },
                            discountCodes.length,
                            response,
                            t,
                            toast,
                            addNotification,
                            `ticket.discountList.${enabled ? 'enable' : 'disabled'}.`
                        );
                    }
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t(`ticket.discountList.${enabled ? 'enable' : 'disabled'}.error`, { count: discountCodes.length }));
                })
                .finally(() => {
                    setSelectedDiscounts({});
                    sync();
                });
        },
        [organization, event, addNotification, toast, sync, setSelectedDiscounts, triggerToast]
    );

    useTrackingPage('VIEW - DISCOUNTS', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });

    const hasAccountWideDiscount = React.useMemo(() => {
        return discountsToDelete.some(discount => discount.discount.is_account_wide);
    }, [discountsToDelete]);

    return (
        <>
            <WeezHeader
                title={t('main_menu_communicate_discount_codes')}
                rightComponent={[
                    <Button
                        primary
                        key="primary"
                        label={t('ticket.discounts.create_new')}
                        icon={<Icon name="plus" />}
                        onClick={() => {
                            track('ACTION > ADD DISCOUNT', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' });
                            history.push(`/ticket/O${organization.id}/E${event.id}/communicate/discounts/create`);
                        }}
                    />,
                    <ButtonDropdown
                        key={'dropdown'}
                        right
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <ExportButton
                                key="export"
                                organization={organization}
                                eventIds={[event.id]}
                                choiceProcess={'discount-codes'}
                                showButton={false}
                                buttonTrans={'ticket.discounts.export'}
                                onError={() => toast.error(t('ticket.export.download_state_error'))}
                            />
                        ]}
                    />
                ]}
                dropdownProps={{}}
            >
                {t('ticket.discounts.pageSubtitle')}
            </WeezHeader>

            {/* <DiscountStatistics organization={organization} event={event} /> */}
            <Card separator={false}>
                <ContentCard>
                    <FilterComponent
                        total={totalDiscounts}
                        loadingCount={loadingCountDiscounts}
                        search={{
                            onChange: (synth, value) => {
                                onChangeSearch(value);
                            },
                            handleReset: (synth, value) => {
                                onChangeSearch(null);
                            },

                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onChange={values => {
                            if (filters?.search !== undefined && filters?.search.length > 0) {
                                values.search = filters.search;
                            }

                            setFilters(values);
                            setCurrentPage(0);
                        }}
                        filters={filters}
                        setFilters={setFilters}
                        initialFilters={initialFilters}
                    />
                    <div className={Style['global-pagesize-container']}>
                        <SelectButton
                            onOpen={() => track('ACTION > ACTION ON SELECTION', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO' })}
                            tooltipTitle={t('ticket.discounts.filter.noActionTooltip')}
                            title={
                                Object.values(selectedDiscounts).length > 0
                                    ? t('ticket.discounts.filter.discountAction', { count: Object.values(selectedDiscounts).length })
                                    : t('ticket.common.action.bulk')
                            }
                            objects={Object.values(selectedDiscounts)}
                        >
                            <DropdownItem item={t('ticket.common.action.activate')} onClick={() => onToggle(Object.values(selectedDiscounts), true)} />
                            <DropdownItem item={t('ticket.common.action.deactivate')} onClick={() => onToggle(Object.values(selectedDiscounts), false)} />
                            {/* <DropdownItem.Line />
                            <DropdownItem item={t('ticket.discounts.filter.edit_discount')} onClick={() => {}} />
                            <DropdownItem item={t('ticket.discounts.filter.programming_validity')} onClick={() => {}} />
                            <DropdownItem item={t('ticket.discounts.filter.change_number_of_use')} onClick={() => {}} /> */}
                            <DropdownItem.Line />
                            <DropdownItem
                                deleteItem
                                item={t('ticket.common.action.delete')}
                                onClick={() => {
                                    track('ACTION > DELETE DISCOUNT', { 'Page Title': 'DISCOUNTS', Application: 'WeezTicket nBO', 'Action type': 'Multiple' });
                                    const objectToDelete = Object.values(selectedDiscounts).map(discount => new DiscountCodeModel(discount));
                                    setDiscountsToDelete(objectToDelete);
                                }}
                            />
                        </SelectButton>
                        <DropdownPageSize
                            current={pageSize}
                            onChange={setPageSize}
                            onChangeCurrentpage={setCurrentPage}
                            totalCount={totalDiscounts === '-' ? 0 : totalDiscounts}
                            loading={loading}
                            labelItem={'ticket.discounts.pageSize'}
                        />
                    </div>
                </ContentCard>
                <Modal
                    open={discountsToDelete.length ? true : false}
                    onClose={() => setDiscountsToDelete([])}
                    title={t('ticket.discountList.table.action.deleteModal.title', {
                        count: discountsToDelete.length
                    })}
                    submitButton={<Button primary onClick={onDelete} label={t('ticket.common.action.confirm')} />}
                    cancelButton={<Button onClick={() => setDiscountsToDelete([])} label={t('ticket.common.action.back')} />}
                >
                    <div>
                        {hasAccountWideDiscount && (
                            <Message type="warning" title={t('ticket.discountList.table.action.deleteModal.message-title')}>
                                {t('ticket.discountList.table.action.deleteModal.message-subtitle')}
                            </Message>
                        )}
                        {t('ticket.discountList.table.action.deleteModal.body', {
                            count: discountsToDelete.length,
                            name: discountsToDelete[0]?.discount?.name || discountsToDelete[0]?.code
                        })}
                    </div>
                </Modal>

                {!loading && (
                    <DiscountTable
                        organization={organization}
                        event={event}
                        discounts={discounts || []}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        totalCount={totalDiscounts === '-' ? 0 : totalDiscounts}
                        onSelected={onSelected}
                        onSelectAll={onSelectAll}
                        setDiscountsToDelete={setDiscountsToDelete}
                        onPageChange={onPageChange}
                        setOrdering={changeOrder}
                        ordering={ordering}
                        sync={() => sync()}
                    />
                )}
                {loading && (
                    <div style={{ height: '20vh' }}>
                        <Loader active style={{ position: 'relative', marginTop: '20px' }}>
                            {t('ticket.common.loading')}
                        </Loader>
                    </div>
                )}
                {!loading &&
                    totalDiscounts === 0 &&
                    (noDiscountCode ? (
                        <EmptyState
                            icon={<Icon name="list" size="regular" />}
                            subtitle={t('ticket.discounts.emptyState.subtitle')}
                            title={t('ticket.discounts.emptyState.title')}
                            labelButton={t('ticket.discounts.emptyState.create_discount')}
                            onClick={() => {
                                history.push(`/ticket/O${organization.id}/E${event.id}/communicate/discounts/create`);
                            }}
                        />
                    ) : (
                        <EmptyState
                            icon={<Icon name="list" size="regular" />}
                            subtitle={t('ticket.discounts.emptyStateFilter.subtitle')}
                            title={t('ticket.discounts.emptyStateFilter.title')}
                        />
                    ))}
            </Card>
        </>
    );
}

DiscountsList.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
